import {Inject, Injectable, LOCALE_ID} from '@angular/core';
import {NativeDateAdapter} from '@sbb-esta/lyne-elements/core/datetime.js';
import {CdDatePipe} from '../../cd-common/custom-pipes/cd-date.pipe';
import {DateService} from '../date/date.service';

@Injectable()
export class CdDateAdapter extends NativeDateAdapter {
  private readonly cdDatePipe: CdDatePipe;

  constructor(@Inject(LOCALE_ID) private readonly locale: string) {
    super();
    this.cdDatePipe = new CdDatePipe(locale);
  }

  override getNumDaysInMonth(date: Date): number {
    return this.getDate(this._createDateWithOverflowOverwrite(this.getYear(date), this.getMonth(date), 0));
  }

  public getAccessibilityFormatDate(date: Date | string): string {
    return this.cdDatePipe.transform(new Date(date), 'longDate');
  }

  override getYear(date: Date): number {
    return DateService.adjustUtcToCH(date).getUTCFullYear();
  }

  override getMonth(date: Date): number {
    return DateService.adjustUtcToCH(date).getUTCMonth() + 1;
  }

  override getDate(date: Date): number {
    return DateService.adjustUtcToCH(date).getUTCDate();
  }

  override getDayOfWeek(date: Date): number {
    return DateService.adjustUtcToCH(date).getUTCDay();
  }

  override format(
    date: Date,
    options: {
      weekdayStyle?: 'long' | 'short' | 'narrow' | 'none';
    }
  ): string {
    if (!this.isValid(date)) return '';
    const utcOffset = DateService.getUTCOffset(date) / 1000 / 60;
    const timeOffsetString = DateService.timezoneOffsetString(utcOffset);
    const dateFormatter = new Intl.DateTimeFormat(this.locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: timeOffsetString
    });
    if (options?.weekdayStyle === 'none') return dateFormatter.format(date);
    const weekdayStyle = options?.weekdayStyle ?? 'short';
    let weekday = this.getDayOfWeekNames(weekdayStyle)[this.getDayOfWeek(date)];
    weekday = weekday.charAt(0).toUpperCase() + weekday.substring(1);
    if (weekdayStyle === 'short') weekday = weekday.substring(0, 2);
    return `${weekday}, ${dateFormatter.format(date)}`;
  }

  override addCalendarYears(date: Date, years: number): Date {
    return DateService.addMonthsToDate(date, years * 12);
  }

  override addCalendarMonths(date: Date, months: number): Date {
    return DateService.addMonthsToDate(date, months);
  }

  override addCalendarDays(date: Date, days: number): Date {
    return DateService.addDaysToDate(date, days);
  }

  override toIso8601(date: Date): string {
    return this.cdDatePipe.transform(date, 'yyyy-MM-dd');
  }

  override today(): Date {
    return DateService.getDayStart(new Date());
  }

  override parse(value: string | null | undefined): Date | null {
    const sanitizedValue = value?.match(/\d{1,2}\.\d{1,2}\.\d{2,4}/)?.[0];
    const parsed = super.parse(sanitizedValue ?? value);
    // prevent more than 4 digit year, as this leads to errors when formatting with Intl.DateTimeFormat.format:
    if (parsed && parsed.getUTCFullYear() > 9999) {
      parsed.setFullYear(parseInt(parsed.getFullYear().toString().substring(0, 4), 10));
    }

    if (parsed && this.getYear(parsed) < 1900) {
      return null;
    }
    return parsed;
  }

  override createDate(year: number, month: number, day: number): Date {
    if (month < 1 || month > 12) throw Error(`Invalid month "${month}". Month has to be between 1 and 12.`);
    else if (day < 1) throw Error(`Invalid day "${day}". Day has to be greater than 0.`);
    const result = this._createDateWithOverflowOverwrite(year, month - 1, day);
    if (DateService.adjustUtcToCH(result).getUTCMonth() + 1 !== month) throw Error(`Invalid day "${day}" for month "${month}".`);
    return result;
  }

  private _createDateWithOverflowOverwrite(year: number, month: number, date: number): Date {
    const result = new Date(Date.UTC(year, month, date));
    // We need to correct for the fact that JS native Date treats years in range [0, 99] as
    // abbreviations for 19xx.
    if (year >= 0 && year < 100) {
      result.setUTCFullYear(result.getUTCFullYear() - 1900);
    }
    return DateService.adjustUtcToCH(result, true);
  }
}
